import { toast } from 'react-toastify';

import { readContract } from '@wagmi/core';

import { ethers } from 'ethers';
import { Address } from 'viem';

import { wagmiConfig, WagmiConfigChain } from '@shared/common/providers/Web3Provider/wagmi';
import {
  BaseTokenDetails,
  namedWeb3Errors,
  SQR_TOKEN_CONTRACT,
  SQR_TOKEN_INFO,
} from '@shared/constants';
import { normalizeError } from '@shared/helpers/displayErrorToast';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import clearSavedConnectionDetails from '@shared/helpers/web3/clearSavedConnectionDetails';
import { numberToWei } from '@shared/helpers/web3/formatters/numberToWei';
import getWalletActionStyles from '@shared/helpers/web3/getWalletActionStyles';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';
import useWeb3 from '@shared/hooks/web3/useWeb3';

const useCheckDepositAllowance = () => {
  const { connectWallet } = useWeb3();
  const { getUserWalletType } = useMagicIdUserWalletInfo();

  const checkIsEnoughDepositAllowance = async (props: {
    amount: number | string;
    targetChain: WagmiConfigChain;
    spenderAddress: string;
    contractAddress?: Address;
    token?: BaseTokenDetails;
  }): Promise<boolean | undefined> => {
    const { token = SQR_TOKEN_INFO, amount, targetChain, spenderAddress, contractAddress } = props;
    const { walletType, userWallet } = getUserWalletType();
    const toastId = Date.now();

    try {
      toast.loading('Checking the deposit allowance...', {
        toastId,
        style: getWalletActionStyles(walletType),
      });

      // Check permission on SQR Token contract by calling read-method allowance(owner, spender)
      // Where owner - user wallet address, spender - SQR Lockup contract address
      const data = await readContract(wagmiConfig, {
        ...SQR_TOKEN_CONTRACT,
        ...(contractAddress && { address: contractAddress }),
        functionName: 'allowance',
        chainId: targetChain.id,
        args: [userWallet.attributes.address, spenderAddress as Address],
      });

      const allowanceInWei = ethers.BigNumber.from(data || '0');
      const amountInWei = numberToWei(amount, token.decimals);

      return allowanceInWei.gte(amountInWei);
    } catch (error) {
      const normalizedError = normalizeError(error);

      const retry = async () => {
        await connectWallet({
          walletType,
          targetAddress: userWallet.attributes.address,
        });

        return checkIsEnoughDepositAllowance(props);
      };

      if (normalizedError.message === namedWeb3Errors.CONNECTOR_NOT_FOUND) {
        clearSavedConnectionDetails();
        return await retry();
      }

      displayWalletErrorToast({
        error,
        unknownExceptionReplaceMessage: namedWeb3Errors.UNKNOWN_TRANSACTION_ERROR,
      });
    } finally {
      toast.dismiss(toastId);
    }
  };

  return { checkIsEnoughDepositAllowance };
};

export default useCheckDepositAllowance;
